import type { UICombatLogEntry } from '@sorrem-client/types';
import { EssenceOrb } from '@sorrem-client/ui/components';
import { useGameplay } from '@sorrem-client/ui/features/PlayPage/gameplay-context';
import { useGameSelector } from '@sorrem-client/ui/hooks';
import { capitalize } from '@sorrem-client/ui/shared/text';
import classNames from 'classnames';
import { useLayoutEffect, useRef } from 'react';
import styles from './CombatLogPanel.module.scss';

/** How close to the bottom (px) still counts as reading the newest lines, so new ones scroll into view. */
const STICK_TO_BOTTOM_PX = 24;

/** Each tone's class in CombatLogPanel.module.scss. */
const TONE_CLASS_NAMES: Record<UICombatLogEntry['tone'], string> = {
    dealt: 'dealt',
    taken: 'taken',
    party: 'party',
    heal: 'heal',
    gain: 'gain',
    death: 'death',
    notice: 'notice',
};

/**
 * The run's combat log (#273), the Log tab's view in the party's HUD area:
 * damage dealt and taken, Essence abilities and what they did, deaths,
 * essence gained and refused casts, newest last. Reads the log itself, so a
 * new line re-renders only this panel. Follows the newest line unless the
 * player has scrolled back.
 */
export function CombatLogPanel(): JSX.Element {
    const { bridge } = useGameplay();
    const entries = useGameSelector(bridge, (state) => state.combatLog);
    const listRef = useRef<HTMLOListElement>(null);
    const isAtBottomRef = useRef(true);

    useLayoutEffect(() => {
        const list = listRef.current;
        if (list !== null && isAtBottomRef.current) {
            list.scrollTop = list.scrollHeight;
        }
    }, [entries]);

    const handleScroll = (): void => {
        const list = listRef.current;
        if (list !== null) {
            isAtBottomRef.current = list.scrollHeight - list.scrollTop - list.clientHeight <= STICK_TO_BOTTOM_PX;
        }
    };

    return (
        <div className={styles.combatLogPanel}>
            {entries.length === 0
                ? <p className={styles.status}>Nothing yet.</p>
                : (
                    <ol ref={listRef} className={styles.entryList} aria-label="Combat log" aria-live="off" onScroll={handleScroll}>
                        {entries.map((entry) => (
                            <li key={entry.id} className={classNames(styles.entry, styles[TONE_CLASS_NAMES[entry.tone]])}>
                                <time className={styles.time} dateTime={new Date(entry.atMs).toISOString()}>
                                    {new Date(entry.atMs).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}
                                </time>
                                {entry.essenceType !== null && (
                                    <span className={styles.essence} title={capitalize(entry.essenceType)}>
                                        <EssenceOrb type={entry.essenceType} className={styles.orb} />
                                    </span>
                                )}
                                <span className={styles.text}>{entry.text}</span>
                            </li>
                        ))}
                    </ol>
                )}
        </div>
    );
}
